import { useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";

import { ONBOARDING_AGENT_NUDGE_TRIGGER_GAP, ONBOARDING_AGENT_TRIGGER_SIZE } from "../../OnboardingAgent/constants";
import {
    resolveAnchoredPlacement,
    resolveAnchoredPosition,
} from "../../OnboardingAgent/helpers/OnboardingAgentPosition.helpers";
import type {
    OnboardingAgentPlacement,
    OnboardingAgentViewportPosition,
} from "../../OnboardingAgent/OnboardingAgent.types";

/** The card sits outside the trigger, so it is pushed away by the trigger's own size plus the gap. */
const NUDGE_OFFSET: OnboardingAgentViewportPosition = {
    x: 0,
    y: ONBOARDING_AGENT_TRIGGER_SIZE + ONBOARDING_AGENT_NUDGE_TRIGGER_GAP,
};

const DEFAULT_PLACEMENT: OnboardingAgentPlacement = { vertical: "above", horizontal: "right" };

type UseOnboardingAgentNudgePlacementResult = {
    readonly slotRef: RefObject<HTMLDivElement | null>;
    readonly slotPosition: OnboardingAgentViewportPosition;
    readonly placement: OnboardingAgentPlacement;
};

/**
 * Places the nudge card next to the trigger, flipping sides when the preferred one would push the
 * card off screen. The card is sized by its content, so it has to be measured before it can be
 * placed — the layout effect lets that correction happen before the browser paints.
 */
export const useOnboardingAgentNudgePlacement = (
    triggerPosition: OnboardingAgentViewportPosition,
    isVisible: boolean,
): UseOnboardingAgentNudgePlacementResult => {
    const slotRef = useRef<HTMLDivElement>(null);
    const [placement, setPlacement] = useState<OnboardingAgentPlacement>(DEFAULT_PLACEMENT);
    const [slotPosition, setSlotPosition] = useState<OnboardingAgentViewportPosition>(triggerPosition);

    useLayoutEffect(() => {
        const slot = slotRef.current;
        if (!isVisible || !slot) {
            return;
        }

        const size = { width: slot.offsetWidth, height: slot.offsetHeight };
        const nextPlacement = resolveAnchoredPlacement(triggerPosition, size, NUDGE_OFFSET);

        setPlacement(nextPlacement);
        setSlotPosition(resolveAnchoredPosition(triggerPosition, size, nextPlacement, NUDGE_OFFSET));
    }, [isVisible, triggerPosition]);

    return { slotRef, slotPosition, placement };
};
